import React, { useMemo } from 'react';
import { HiArrowRight } from 'react-icons/hi2';
import Button from './Button';

export interface ConflictNavigateTarget {
  week?: number | null;
  year?: number | null;
  itemIds: Array<string | number>;
  day?: string;
}

interface ConflictPopoverReason {
  reason: string;
  severity?: string;
  reasonId?: string;
  dismissed?: boolean;
  week?: number;
  year?: number;
  conflictingEntryId?: string;
  isCrossPeriod?: boolean;
  otherPeriodName?: string;
}

interface ConflictPopoverProps {
  reasons: ConflictPopoverReason[];
  position: { x: number; y: number };
  onClose?: () => void;
  onDismissAll?: () => void;
  popoverRef?:
  | React.RefObject<HTMLDivElement>
  | React.RefObject<HTMLDivElement | null>
  | React.MutableRefObject<HTMLDivElement | null>;
  onToggleReason?: (reasonId: string) => void;
  showDismissed?: boolean;
  onToggleShowDismissed?: () => void;
  itemId?: string | number | null;
  day?: string;
  currentWeekNumber?: number;
  currentYear?: number;
  onNavigateToConflict?: (target: ConflictNavigateTarget) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const ArrowIcon: React.FC<{ className?: string }> = ({ className }) => {
  const Icon = HiArrowRight as unknown as React.ComponentType<{ className?: string }>;
  return <Icon className={className} />;
};

const weekKey = (week?: number | null, year?: number | null) => `${week ?? ''}-${year ?? ''}`;

const stripWeekFromReason = (reason: string) => (
  (reason || '')
    .replace(/^Week \d+(\/\d+)?:\s*/i, '')
    .replace(/, week \d+/i, '')
    .replace(/\(week \d+\)/i, '')
    .trim()
);

const parseWeekFromReason = (reason: string) => {
  const prefixed = reason.match(/^Week (\d+)(?:\/(\d+))?:/i);
  if (prefixed) {
    return {
      week: parseInt(prefixed[1] ?? '0', 10),
      year: prefixed[2] ? parseInt(prefixed[2], 10) : null
    };
  }
  const inline = reason.match(/week (\d+)/i);
  return {
    week: inline?.[1] ? parseInt(inline[1], 10) : null,
    year: null as number | null
  };
};

const getReasonType = (reasonText: string, isCrossPeriod?: boolean) => {
  if (isCrossPeriod) return 'Cross-period';
  const lower = (reasonText || '').toLowerCase();
  if (lower.includes('holiday')) return 'Holiday';
  if (lower.includes('room') && lower.includes('occupied')) return 'Room occupied';
  if (lower.includes('room') && lower.includes('not suitable')) return 'Room not suitable';
  if (lower.includes('teacher') && lower.includes('unavailable')) return 'Teacher unavailable';
  if (lower.includes('teacher') && lower.includes('prefers away')) return 'Teacher prefers away';
  if (lower.includes('teacher') && lower.includes('conflicting class')) return 'Teacher conflict';
  if (lower.includes('group') && lower.includes('unavailable')) return 'Group unavailable';
  if (lower.includes('group') && lower.includes('prefers away')) return 'Group prefers away';
  if (lower.includes('group') && lower.includes('conflicting class')) return 'Group conflict';
  if (lower.includes('invalid input')) return 'Data issue';
  return 'Conflict';
};

const ConflictPopover: React.FC<ConflictPopoverProps> = ({
  reasons,
  position,
  onClose,
  onDismissAll,
  popoverRef,
  onToggleReason,
  showDismissed = false,
  onToggleShowDismissed,
  itemId = null,
  day,
  currentWeekNumber,
  currentYear,
  onNavigateToConflict,
  onMouseEnter,
  onMouseLeave,
}) => {
  const style: React.CSSProperties = {
    position: 'fixed',
    left: `${position.x}px`,
    top: `${position.y}px`,
    zIndex: 200,
  };

  const groupedReasons = useMemo(() => {
    const groups = new Map<string, {
      baseMessage: string;
      severity?: string;
      reasonId?: string;
      dismissed: boolean;
      isCrossPeriod: boolean;
      otherPeriodName?: string;
      occurrences: Map<string, { week: number | null; year: number | null; conflictingEntryIds: Set<string> }>;
    }>();

    (Array.isArray(reasons) ? reasons : []).forEach((reason) => {
      const parsed = parseWeekFromReason(reason.reason);
      const week = reason.week ?? parsed.week;
      const year = reason.year ?? parsed.year;
      const baseMessage = stripWeekFromReason(reason.reason);
      const groupKey = reason.reasonId || baseMessage;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          baseMessage,
          severity: reason.severity,
          reasonId: reason.reasonId,
          dismissed: Boolean(reason.dismissed),
          isCrossPeriod: Boolean(reason.isCrossPeriod),
          otherPeriodName: reason.otherPeriodName,
          occurrences: new Map(),
        });
      }

      const group = groups.get(groupKey)!;
      if (reason.severity === 'red') group.severity = 'red';
      if (reason.isCrossPeriod) {
        group.isCrossPeriod = true;
        if (reason.otherPeriodName) group.otherPeriodName = reason.otherPeriodName;
      }
      if (reason.dismissed) group.dismissed = true;

      const occKey = weekKey(week, year);
      if (!group.occurrences.has(occKey)) {
        group.occurrences.set(occKey, {
          week,
          year,
          conflictingEntryIds: new Set(),
        });
      }
      if (reason.conflictingEntryId) {
        group.occurrences.get(occKey)!.conflictingEntryIds.add(reason.conflictingEntryId);
      }
    });

    return Array.from(groups.values()).map((group) => ({
      ...group,
      occurrences: Array.from(group.occurrences.values())
        .filter((occ) => occ.week != null)
        .sort((a, b) => {
          if (a.year != null && b.year != null && a.year !== b.year) return a.year - b.year;
          return (a.week || 0) - (b.week || 0);
        })
        .map((occ) => ({
          week: occ.week,
          year: occ.year,
          conflictingEntryIds: Array.from(occ.conflictingEntryIds),
        })),
      type: getReasonType(group.baseMessage, group.isCrossPeriod),
    }));
  }, [reasons]);

  const dismissedCount = groupedReasons.filter((r) => r.dismissed).length;
  const hasActiveReasons = groupedReasons.some((r) => !r.dismissed);
  const visibleReasons = showDismissed
    ? groupedReasons.filter((r) => r.dismissed)
    : groupedReasons.filter((r) => !r.dismissed);

  const pickOccurrence = (reason: (typeof groupedReasons)[number]) => {
    const occurrences = reason.occurrences || [];
    if (occurrences.length === 0) {
      return { week: currentWeekNumber ?? null, year: currentYear ?? null, conflictingEntryIds: [] as string[] };
    }
    const current = occurrences.find((occ) => (
      occ.week === currentWeekNumber && (occ.year == null || occ.year === currentYear)
    ));
    return current
      ?? occurrences[0]
      ?? { week: currentWeekNumber ?? null, year: currentYear ?? null, conflictingEntryIds: [] as string[] };
  };

  const navigateToReason = (
    reason: (typeof groupedReasons)[number],
    occurrence?: (typeof groupedReasons)[number]['occurrences'][number]
  ) => {
    if (!onNavigateToConflict) return;
    const target = occurrence ?? pickOccurrence(reason);
    const itemIds = [itemId, ...(target.conflictingEntryIds || [])].filter(Boolean) as Array<string | number>;
    onNavigateToConflict({
      week: target.week,
      year: target.year ?? currentYear,
      itemIds,
      day,
    });
  };

  return (
    <div
      ref={popoverRef}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="w-[22rem] max-w-[calc(100vw-1.5rem)] rounded-lg border border-gray-200 bg-white p-3 shadow-xl space-y-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-gray-800">Conflicts</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {visibleReasons.length} {showDismissed ? 'dismissed' : 'active'}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {onDismissAll && hasActiveReasons && (
            <Button
              variant="ghost"
              size="sm"
              title="Dismiss all"
              aria-label="Dismiss all conflicts"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); onDismissAll(); }}
            >
              Dismiss all
            </Button>
          )}
          {dismissedCount > 0 && (
            <Button
              variant={showDismissed ? 'secondary' : 'ghost'}
              size="sm"
              title={showDismissed ? 'Show active conflicts' : 'Show dismissed conflicts'}
              aria-label={showDismissed ? 'Show active conflicts' : 'Show dismissed conflicts'}
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); onToggleShowDismissed?.(); }}
            >
              {showDismissed ? 'Show active' : `Dismissed (${dismissedCount})`}
            </Button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            aria-label="Close conflict popover"
            className="text-gray-400 hover:text-gray-700 text-xl leading-none px-1"
          >
            ×
          </button>
        </div>
      </div>

      {visibleReasons.length > 0 ? (
        <ul className="space-y-2 max-h-72 overflow-y-auto pr-0.5">
          {visibleReasons.map((reason, index) => {
            const severityBorder = reason.severity === 'red' ? 'border-l-red-500' : 'border-l-orange-400';
            const severityText = reason.severity === 'red' ? 'text-red-700' : 'text-orange-700';

            return (
              <li
                key={reason.reasonId || index}
                className={`rounded-md border border-gray-200 border-l-4 ${severityBorder} bg-gray-50 p-2.5`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`text-[11px] font-medium ${severityText}`}>
                        {reason.type}
                      </span>
                      {reason.isCrossPeriod && reason.otherPeriodName && (
                        <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700">
                          Other period · {reason.otherPeriodName}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm text-gray-700 ${reason.dismissed ? 'line-through text-gray-400' : ''}`}>
                      {reason.baseMessage}
                    </p>
                    {reason.occurrences.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {reason.occurrences.map((occ) => {
                          const isActiveWeek = occ.week === currentWeekNumber
                            && (occ.year == null || occ.year === currentYear);
                          return (
                            <button
                              key={weekKey(occ.week, occ.year)}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigateToReason(reason, occ); }}
                              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${isActiveWeek
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-blue-50 hover:text-blue-700'
                                }`}
                              title="Show this week on the schedule"
                            >
                              {occ.year ? `${occ.year} · ` : ''}Week {occ.week}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-stretch gap-1">
                    {onNavigateToConflict && (
                      <Button
                        variant="primary"
                        size="sm"
                        title="Show on schedule"
                        aria-label="Show conflicting entries on the schedule"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigateToReason(reason); }}
                        className="inline-flex items-center justify-center gap-1 whitespace-nowrap"
                      >
                        <ArrowIcon className="h-3.5 w-3.5" />
                        View
                      </Button>
                    )}
                    {onToggleReason && reason.reasonId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        title={reason.dismissed ? 'Restore conflict' : 'Dismiss conflict'}
                        aria-label={reason.dismissed ? 'Restore conflict' : 'Dismiss conflict'}
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); onToggleReason(reason.reasonId!); }}
                      >
                        {reason.dismissed ? 'Restore' : 'Dismiss'}
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">
          {dismissedCount > 0 && !showDismissed
            ? 'No active conflicts. Show dismissed to view them.'
            : 'No conflicts to display.'}
        </p>
      )}
    </div>
  );
};

export default ConflictPopover;
